import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Users, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import api from '../../services/api';

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

const SproutSyncConfig = ({ campaignId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [schemaReady, setSchemaReady] = useState(false);
  const [hasToken, setHasToken] = useState(false);
  const [maskedToken, setMaskedToken] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [tagOverride, setTagOverride] = useState('');
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(true);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [lastSyncStatus, setLastSyncStatus] = useState(null);
  const [stats, setStats] = useState({
    total_subscribers: 0,
    synced_count: 0,
    pending_count: 0,
    failed_count: 0
  });
  const [recentRuns, setRecentRuns] = useState([]);

  const applySettings = useCallback((data) => {
    setSchemaReady(Boolean(data?.schema_ready));
    setHasToken(Boolean(data?.has_token));
    setMaskedToken(data?.masked_token || '');
    setTagOverride(data?.tag_override || '');
    setAutoSyncEnabled(data?.auto_sync_enabled !== false);
    setLastSyncAt(data?.last_sync_at || null);
    setLastSyncStatus(data?.last_sync_status || null);
    setStats(data?.stats || {
      total_subscribers: 0,
      synced_count: 0,
      pending_count: 0,
      failed_count: 0
    });
    setRecentRuns(Array.isArray(data?.recent_runs) ? data.recent_runs : []);
  }, []);

  const fetchSettings = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(`/admin/campaigns/${campaignId}/sprout-settings`);
      applySettings(data);
    } catch (err) {
      console.error('Fetch sprout settings error:', err);
      setError(err.response?.data?.error || 'Failed to load SproutSend settings');
    } finally {
      setLoading(false);
    }
  }, [campaignId, applySettings]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        tag_override: tagOverride,
        auto_sync_enabled: autoSyncEnabled
      };
      if (accessToken.trim()) {
        payload.access_token = accessToken.trim();
      } else if (!hasToken) {
        setError('Please enter a SproutSend API token');
        setSaving(false);
        return;
      }

      const { data } = await api.put(`/admin/campaigns/${campaignId}/sprout-settings`, payload);
      setHasToken(Boolean(data?.has_token));
      setMaskedToken(data?.masked_token || '');
      setAccessToken('');
      setTagOverride(data?.tag_override || '');
      setAutoSyncEnabled(data?.auto_sync_enabled !== false);
      setLastSyncAt(data?.last_sync_at || null);
      setLastSyncStatus(data?.last_sync_status || null);
      setSuccess('SproutSend settings saved.');
      await fetchSettings();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save SproutSend settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setError('');
    setSuccess('');
    try {
      const { data } = await api.post(`/admin/campaigns/${campaignId}/sprout/sync`);
      applySettings({
        schema_ready: true,
        has_token: hasToken,
        masked_token: maskedToken,
        tag_override: tagOverride,
        auto_sync_enabled: autoSyncEnabled,
        last_sync_at: data?.run?.status ? new Date().toISOString() : lastSyncAt,
        last_sync_status: data?.run?.status || lastSyncStatus,
        stats: data?.stats,
        recent_runs: data?.recent_runs
      });
      setSuccess(
        `Sync complete: ${data?.run?.synced_count ?? 0} synced, ${data?.run?.failed_count ?? 0} failed.`
      );
      await fetchSettings();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to sync contacts to SproutSend');
    } finally {
      setSyncing(false);
    }
  };

  const tokenStatus = hasToken ? 'connected' : 'needs-token';

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-3">
        <label className="block text-sm font-semibold text-gray-900">
          me&amp;u CRM (SproutSend) Integration
        </label>
        <span
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
            tokenStatus === 'connected'
              ? 'bg-green-100 text-green-800'
              : 'bg-amber-100 text-amber-800'
          }`}
        >
          {tokenStatus === 'connected' ? (
            <CheckCircle2 className="w-3.5 h-3.5" />
          ) : (
            <AlertCircle className="w-3.5 h-3.5" />
          )}
          {tokenStatus === 'connected' ? 'Connected' : 'Needs token'}
        </span>
      </div>

      <p className="text-xs text-gray-500 mb-4">
        Sync newsletter subscribers from this campaign to me&amp;u CRM (SproutSend). Contacts come from
        subscribers who opted in during entry.
      </p>

      {!schemaReady && !loading && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl text-sm">
          SproutSend database tables are not ready yet. Run the migration SQL snippets from the plan before saving or syncing.
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-xl text-sm">
          {success}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 text-sm py-4">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Loading SproutSend settings…
        </div>
      ) : (
        <>
          <form onSubmit={handleSave} className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API Token
              </label>
              <input
                type="password"
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                placeholder={hasToken ? `Saved token ${maskedToken}` : 'Paste SproutSend bearer token'}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
              {hasToken && (
                <p className="text-xs text-gray-500 mt-2">
                  Current token: <code className="font-mono">{maskedToken}</code>. Leave blank to keep the existing token.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tag override (optional)
              </label>
              <input
                type="text"
                value={tagOverride}
                onChange={(e) => setTagOverride(e.target.value)}
                placeholder="Defaults to campaign name"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            <label className="flex items-center gap-3 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={autoSyncEnabled}
                onChange={(e) => setAutoSyncEnabled(e.target.checked)}
                className="rounded border-gray-300 text-teal-700 focus:ring-teal-500"
              />
              Auto-sync new newsletter signups
            </label>

            <button
              type="submit"
              disabled={saving || !schemaReady}
              className="px-5 py-2.5 bg-teal-700 text-white rounded-xl hover:bg-teal-600 disabled:opacity-50 text-sm font-medium"
            >
              {saving ? 'Saving…' : 'Save SproutSend Settings'}
            </button>
          </form>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                <Users className="w-3.5 h-3.5" />
                Total subscribers
              </div>
              <p className="text-2xl font-semibold text-gray-900">{stats.total_subscribers}</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-green-700 text-xs mb-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Synced
              </div>
              <p className="text-2xl font-semibold text-green-900">{stats.synced_count}</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-amber-700 text-xs mb-1">
                <Clock className="w-3.5 h-3.5" />
                Pending
              </div>
              <p className="text-2xl font-semibold text-amber-900">{stats.pending_count}</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-red-700 text-xs mb-1">
                <AlertCircle className="w-3.5 h-3.5" />
                Failed
              </div>
              <p className="text-2xl font-semibold text-red-900">{stats.failed_count}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="text-sm text-gray-600">
              Last sync: <span className="font-medium text-gray-900">{formatDateTime(lastSyncAt)}</span>
              {lastSyncStatus && (
                <span className="ml-2 text-xs uppercase tracking-wide text-gray-500">({lastSyncStatus})</span>
              )}
            </div>
            <button
              type="button"
              onClick={handleSync}
              disabled={syncing || !hasToken || !schemaReady}
              className="px-5 py-2.5 bg-teal-700 text-white rounded-xl hover:bg-teal-600 disabled:opacity-50 text-sm font-medium flex items-center gap-2"
            >
              {syncing ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
              {syncing ? 'Syncing…' : 'Sync Contacts'}
            </button>
          </div>

          {recentRuns.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Started</th>
                    <th className="px-4 py-2 text-left font-medium">Status</th>
                    <th className="px-4 py-2 text-right font-medium">Total</th>
                    <th className="px-4 py-2 text-right font-medium">Synced</th>
                    <th className="px-4 py-2 text-right font-medium">Failed</th>
                  </tr>
                </thead>
                <tbody>
                  {recentRuns.map((run) => (
                    <tr key={run.id} className="border-t border-gray-100">
                      <td className="px-4 py-2 text-gray-700">{formatDateTime(run.started_at)}</td>
                      <td className="px-4 py-2 capitalize text-gray-700">{run.status}</td>
                      <td className="px-4 py-2 text-right text-gray-700">{run.total_contacts}</td>
                      <td className="px-4 py-2 text-right text-green-700">{run.synced_count}</td>
                      <td className="px-4 py-2 text-right text-red-700">{run.failed_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SproutSyncConfig;
